import React, { useContext, useState } from "react"
import { Dialog } from "@reach/dialog"
import "@reach/dialog/styles.css"
import { useUserPlaylists, UserPlaylist } from "../../hooks/useUserPlaylists"
import { addSongToPlaylist, createPlaylist } from "../../util/backend_api/playlists"
import { UserContext } from "../HomeApp"
import { Spinner } from "../shared/Spinner"
import { toast } from "react-toastify"

interface AddToPlaylistModalProps {
  isOpen: boolean
  onClose: () => void
  songId: number
  songTitle: string
}

const AddToPlaylistModal: React.FC<AddToPlaylistModalProps> = ({
  isOpen,
  onClose,
  songId,
  songTitle,
}) => {
  const { user } = useContext(UserContext)
  const [filter, setFilter] = useState("")
  const [addingId, setAddingId] = useState<number | null>(null)
  const [addedIds, setAddedIds] = useState<number[]>([])
  const [creatingAndAdding, setCreatingAndAdding] = useState(false)

  const { data: playlistData, isLoading } = useUserPlaylists(user?.id) as {
    data?: { playlists?: UserPlaylist[] }
    isLoading: boolean
  }

  const allPlaylists = playlistData?.playlists ?? []
  const filtered = filter.trim()
    ? allPlaylists.filter((p) =>
        p.name?.toLowerCase().includes(filter.trim().toLowerCase()),
      )
    : allPlaylists

  const handleAdd = async (playlist: UserPlaylist) => {
    setAddingId(playlist.id)
    await addSongToPlaylist(playlist.id, songId)
    setAddingId(null)
    setAddedIds((prev) => [...prev, playlist.id])
    toast.success(`"${songTitle}" was added to ${playlist.name}`, {
      position: "top-center",
      autoClose: 2000,
      hideProgressBar: true,
      closeOnClick: true,
    })
  }

  const handleCreateAndAdd = async () => {
    const name = filter.trim()
    setCreatingAndAdding(true)
    const result = await createPlaylist({ name, description: "" })
    const newPlaylistId = result?.playlist?.id
    if (newPlaylistId) {
      await addSongToPlaylist(newPlaylistId, songId)
      toast.success(`"${songTitle}" was added to ${name}`, {
        position: "top-center",
        autoClose: 2000,
        hideProgressBar: true,
        closeOnClick: true,
      })
    }
    setCreatingAndAdding(false)
    handleClose()
  }

  const handleClose = () => {
    setFilter("")
    setAddedIds([])
    onClose()
  }

  return (
    <Dialog
      isOpen={isOpen}
      onDismiss={handleClose}
      aria-label="Add song to playlist"
      style={{ maxWidth: 480, width: "90%", padding: 0, borderRadius: 2 }}
    >
      {/* Header */}
      <div className="flex items-center justify-between px-3 py-2 border-b border-gray-200">
        <div>
          <h2 className="text-sm font-semibold text-gray-900">Add to Playlist</h2>
          <p className="text-xs text-gray-500 truncate">{songTitle}</p>
        </div>
        <button
          type="button"
          onClick={handleClose}
          className="text-gray-400 hover:text-gray-600 focus:outline-none text-xl leading-none"
        >
          ×
        </button>
      </div>

      {/* Filter input */}
      <div className="px-3 py-2 border-b border-gray-200">
        <input
          type="text"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder="Filter playlists…"
          className="appearance-none border border-gray-300 rounded-sm w-full text-sm py-1 px-2 leading-tight focus:outline-none focus:border-gray-500 text-gray-900"
          autoFocus
        />
      </div>

      {/* Playlist list */}
      <div style={{ maxHeight: 280, overflowY: "auto" }}>
        {isLoading ? (
          <div className="flex justify-center py-4">
            <Spinner />
          </div>
        ) : filtered.length === 0 ? (
          <div className="px-3 py-2">
            <p className="text-sm text-gray-500 mb-2">
              {filter ? "No playlists match your search." : "No playlists yet."}
            </p>
            {filter.trim() && (
              <div>
                <button
                  type="button"
                  disabled={creatingAndAdding}
                  onClick={handleCreateAndAdd}
                  className="text-xs font-medium border border-gray-500 text-gray-700 px-3 py-1 rounded-sm focus:outline-none disabled:opacity-50"
                >
                  {creatingAndAdding ? "Creating…" : `Create "${filter.trim()}"`}
                </button>
                <p className="text-xs text-gray-400 italic mt-1">
                  {songTitle} will be added as well
                </p>
              </div>
            )}
          </div>
        ) : (
          <ul>
            {filtered.map((playlist) => {
              const added = addedIds.includes(playlist.id)
              const loading = addingId === playlist.id

              return (
                <li
                  key={playlist.id}
                  className="flex items-center justify-between px-3 py-2 border-b border-gray-100 last:border-0"
                >
                  <span className="text-sm text-gray-800 truncate pr-4">
                    {playlist.name}
                  </span>
                  {added ? (
                    <span className="text-xs text-green-600 font-medium flex-none">
                      Added ✓
                    </span>
                  ) : (
                    <button
                      type="button"
                      disabled={loading}
                      onClick={() => handleAdd(playlist)}
                      className="flex-none text-xs font-medium border border-gray-500 text-gray-700 px-3 py-1 rounded-sm focus:outline-none disabled:opacity-50"
                    >
                      {loading ? "Adding…" : "Add Song"}
                    </button>
                  )}
                </li>
              )
            })}
          </ul>
        )}
      </div>
    </Dialog>
  )
}

export default AddToPlaylistModal
